import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export interface IndexAverageByDateRangeResponse {
  [date: string]: number; // DateOnly as string -> average value
}

export type IndexType = 'Ndvi' | 'Evi' | 'Gndvi' | 'Mndwi' | 'Ndwi' | 'Ndmi' | 'Orvi' | 'Osavi' | 'Arvi';

export interface IndexAvailableDatesRequest {
  cropId: string;
  regionId?: string;
  startDate?: string;
  endDate?: string;
}

export interface RequestGetIndexDatesFromGeoServerFolders {
  IndexName: string;
  year:number
}

@Injectable({
  providedIn: 'root'
})
export class SatelliteIndicesService {

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  /**
   * Получает среднее значение индекса по региону за период времени
   * @param indexType Тип индекса (Ndvi, Evi, Gndvi и т.д.)
   * @param startDate Начальная дата периода
   * @param endDate Конечная дата периода
   * @param regionId Идентификатор региона (опционально)
   * @returns Observable с Dictionary где ключ - дата (DateOnly), значение - средний индекс
   */
  getIndexAverageByRegionAndDateRange(
    indexType: IndexType,
    startDate: Date,
    endDate: Date,
    regionId?: string | null
  ): Observable<Record<string, number>> {
    const apiUrl = `${environment.api}/satellite/${indexType}/GetIndexAverageByRegionAndDateRange`;
    const token = this.authService.getToken();

    let headers = new HttpHeaders();
    if (token) {
       headers = headers.set('Authorization', `Bearer ${token}`);
     }

     // Format date to YYYY-MM-DD for DateOnly
     const formatDate = (date: Date): string => {
       const year = date.getFullYear();
       const month = String(date.getMonth() + 1).padStart(2, '0');
       const day = String(date.getDate()).padStart(2, '0');
       return `${year}-${month}-${day}`;
     };

     let params = new HttpParams()
       .set('startDate', formatDate(startDate))
       .set('endDate', formatDate(endDate));

     if (regionId) {
       params = params.set('regionId', regionId);
     }

     return this.http.post<Record<string, number>>(apiUrl, {}, { headers, params }).pipe(
       map((response) => {
         // Transform keys from strings to DateOnly format if needed
         return response || {};
      })
    );
  }

  /**
   * Получает SharedReports для индекса с фильтрами
   * @param indexType Тип индекса
   * @param query Запрос с фильтрами
   * @returns Observable со списком отчетов
   */
  getIndexSharedReports(
    indexType: IndexType,
    query: any
  ): Observable<any[]> {
    const apiUrl = `${environment.api}/satellite/${indexType}/GetIndexSharedReports`;
    const token = this.authService.getToken();

    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

        console.log(`🌐 API Request: ${apiUrl}`, query);

         return this.http.post<any>(apiUrl, query, { headers }).pipe(
             map((response: any) => {
                 // Handle different response formats
                 let reports: any[] = [];
                 if (Array.isArray(response)) {
                     reports = response;
                 } else if (response && typeof response === 'object' && 'data' in response && Array.isArray(response.data)) {
                     reports = response.data;
                 } else if (response && typeof response === 'object' && Array.isArray(response)) {
                     reports = response;
                 }

                 console.log(`📥 API Response: ${reports.length} reports for ${indexType}`);

                 // Normalize data - ensure all required fields are present
                 return reports.map((report: any) => ({
                    ...report,
                    regionId: report.regionId || report.RegionId || null,
                    cropId: report.cropId || report.CropId || null,
                    average: report.average !== undefined ? report.average : (report.Average !== undefined ? report.Average : null),
                    dateOnly: report.dateOnly || report.DateOnly || report.date || null
                }));
            })
        );
  }

  /**
   * Получает список дат, для которых доступны SharedReports
   */
  getIndexAvailableDates(
    indexType: IndexType,
    request: IndexAvailableDatesRequest
  ): Observable<string[]> {
    const apiUrl = `${environment.api}/satellite/${indexType}/GetIndexAvailableDates`;
    const token = this.authService.getToken();

    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return this.http.post<string[]>(apiUrl, request, { headers }).pipe(
      map((response) => response || [])
    );
  }

  GetIndexDatesFromTileLayers(
    indexType: IndexType,
    request: RequestGetIndexDatesFromGeoServerFolders
  ): Observable<string[]> {
    const apiUrl = `${environment.api}/satellite/${indexType}/GetIndexDatesFromTileLayers`;
    const token = this.authService.getToken();

    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return this.http.post<string[]>(apiUrl, request, { headers }).pipe(
      map((response) => response || [])
    );
  }
}



